import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { KlientService } from '../../../@core/http/klient.service';
import { Moduly, Klient, TypKlienta } from '../../../shared/models/klient.model';
import { TokenPayload } from '../../../shared/models/token-payload.model';

export interface MenuItem {
  title: string;
  icon: string;
  link?: string;
  home?: boolean;
  hidden?: boolean;
  external?: boolean;
  expandable?: boolean;
  action?: string;
}

@Component({
  selector: 'ngx-main-menu-sidebar',
  templateUrl: './main-menu-sidebar.component.html',
  styleUrls: ['./main-menu-sidebar.component.scss']
})
export class MainMenuSidebarComponent implements OnInit {
  @Input() isOpen = false;
  @Input() user: TokenPayload;
  @Output() onClose = new EventEmitter<void>();
  @Output() onLogout = new EventEmitter<void>();

  menu: MenuItem[] = [];
  isAccountSidebarOpen = false;
  private moduly: Moduly;
  private klient: Klient;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private klientService: KlientService
  ) { }

  ngOnInit(): void {
    this.klientService.getKlient().subscribe(klient => {
      this.klient = klient;
      this.updateMenuItems();
    });

    this.klientService.getModuly().subscribe(response => {
      this.moduly = response;
      this.updateMenuItems();
    });

    this.translate.onLangChange.subscribe(() => {
      this.updateMenuItems();
    });
  }

  closeSidebar() {
    this.onClose.emit();
  }

  navigateToRoute(item: MenuItem) {
    if (item.action === 'toggleAccount') {
      this.toggleAccountSidebar();
      // Close main menu sidebar on mobile when account sidebar opens
      if (this.isMobile) {
        this.closeSidebar();
      }
      return;
    }

    if (item.link) {
      if (item.external) {
        window.open(item.link, '_blank');
      } else {
        this.router.navigate([item.link]);
      }
      // Only close sidebar on mobile, keep open on desktop
      if (this.isMobile) {
        this.closeSidebar();
      }
    }
  }

  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  toggleAccountSidebar() {
    this.isAccountSidebarOpen = !this.isAccountSidebarOpen;
  }

  closeAccountSidebar() {
    this.isAccountSidebarOpen = false;
  }

  onAccountSidebarLogout() {
    this.onLogout.emit();
    this.closeAccountSidebar();
  }

  private updateMenuItems() {
    this.menu = this.getMenuItems().map(item => ({
      ...item,
      title: this.translate.instant(item.title)
    }));
  }

  private czyModulNieaktywny(modul: string): boolean {
    return this.moduly?.nieaktywne?.includes(modul) || false;
  }

  private czyKlientTypuKF(): boolean {
    return this.klient?.typ === TypKlienta.KF;
  }

  private czyKlientTypuKO(): boolean {
    return this.klient?.typ === TypKlienta.KO;
  }

  private czyKlientKZ(): boolean {
    return this.klient?.typ === TypKlienta.KZ;
  }

  private getMenuItems(): MenuItem[] {
    const menuItems: MenuItem[] = [];

    // Add "Strona główna" for KF clients
    if (this.czyKlientTypuKF()) {
      menuItems.push({
        title: 'menu.strona-glowna',
        icon: 'home-outline',
        link: '/pages/home-dg',
        home: true,
      });
    } else if (this.czyKlientTypuKO()) {
      menuItems.push({
        title: 'menu.strona-glowna',
        icon: 'home-outline',
        link: '/pages/home-kh',
        home: true,
      });

      // Add KO-specific menu items
      menuItems.push({
        title: 'menu.rachunek-wynikow',
        icon: 'bar-chart-outline',
        link: '/pages/rachunek-wynikow',
      });

      menuItems.push({
        title: 'menu.bilans',
        icon: 'clipboard-outline',
        link: '/pages/bilans',
      });
    }

    // Add standard menu items
    menuItems.push(
      {
        title: 'menu.przychody-koszty',
        icon: 'trending-up-outline',
        link: '/pages/przychody-koszty',
        home: this.czyKlientKZ(),
        hidden: this.czyKlientTypuKO() || this.czyModulNieaktywny('kpir')
      },
      {
        title: 'menu.sprzedaz',
        link: '/pages/sprzedaz',
        icon: 'diagonal-arrow-right-up-outline',
        home: this.czyKlientTypuKO()
      },
      {
        title: 'menu.zakup',
        link: '/pages/zakup',
        icon: 'diagonal-arrow-right-down-outline',
      },
      {
        title: 'menu.kpir',
        link: '/pages/kpir',
        icon: 'list-outline',
        hidden: this.czyModulNieaktywny('kpir'),
      },
      {
        title: 'menu.ewidencja-ryczaltu',
        link: '/pages/ryczalt',
        icon: 'list-outline',
        hidden: this.czyModulNieaktywny('ryczalt') || this.czyKlientTypuKO(),
      },
      {
        title: 'menu.podatki-zus',
        icon: 'percent-outline',
        link: '/pages/zus-podatki',
      },
      {
        title: 'menu.wynagrodzenia',
        icon: 'gift-outline',
        link: '/pages/wynagrodzenia',
      },
      {
        title: 'menu.platnosci',
        icon: 'shopping-cart-outline',
        link: '/pages/platnosci',
      },
      {
        title: 'menu.saldeo-dodaj-dokument',
        icon: 'log-in-outline',
        link: '/pages/saldeo',
      },
      {
        title: 'menu.saldeo-wystaw-fakture',
        icon: 'log-in-outline',
        link: '/pages/saldeo',
      },
      {
        title: 'menu.poradnik-podatkowy',
        icon: 'book-outline',
        link: 'https://dwaplusjeden.com/blog/',
        external: true,
      },
      {
        title: 'menu.wskazowki-i-instrukcje',
        icon: 'file-text-outline',
        link: '/pages/materialy',
      },
      {
        title: 'menu.kontakt',
        icon: 'phone-outline',
        link: '/pages/kontakt',
      },
      {
        title: 'menu.konto',
        icon: 'person-outline',
        expandable: true,
        action: 'toggleAccount',
      }
    );

    return menuItems.filter(item => !item.hidden);
  }
}
